import React, { useState, useMemo } from 'react';
import {
  Box,
  Pagination,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  InputAdornment,
  Card,
  Chip,
  Stack,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Grid,
  Tooltip,
  Collapse,
  Divider,
} from '@mui/material';
import {
  Refresh,
  Search,
  FilterList,
  Delete,
  Close,
  Assessment,
  PictureAsPdf,
  TableChart,
  Article,
  CalendarToday,
  KeyboardArrowDown,
  KeyboardArrowUp,
  FileDownload,
  PlayArrow,
  CheckCircle,
  HourglassEmpty,
  ErrorOutline,
  Summarize,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useListReportsQuery,
  useGenerateReportMutation,
  useDeleteReportMutation,
  GeneratedReport,
  REPORT_TYPE_FILTER_OPTIONS,
  REPORT_STATUS_OPTIONS,
} from '../../features/admin/generatedReportsApi.ts';

import {
  useListReportFormatsQuery,
  ReportFormat,
} from '../../features/admin/reportListApi.ts';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-AE', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return dateStr; }
};

const formatDateTime = (dateStr?: string) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleString('en-AE', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return dateStr; }
};

const getStatusChip = (status: string) => {
  switch (status) {
    case 'ready':
      return { bgcolor: '#d1f4e0', color: '#00783e', icon: <CheckCircle sx={{ fontSize: 12 }} />, label: 'Ready' };
    case 'generating':
      return { bgcolor: '#fff3cd', color: '#856404', icon: <HourglassEmpty sx={{ fontSize: 12 }} />, label: 'Generating' };
    case 'failed':
      return { bgcolor: '#ffebee', color: '#c62828', icon: <ErrorOutline sx={{ fontSize: 12 }} />, label: 'Failed' };
    default:
      return { bgcolor: '#f1f2f4', color: '#6d7175', icon: null, label: status };
  }
};

const getExportColor = (type: string) => {
  if (type === 'pdf')   return { bgcolor: '#ffebee', color: '#c62828' };
  if (type === 'csv')   return { bgcolor: '#e8f5e9', color: '#2e7d32' };
  if (type === 'both')  return { bgcolor: '#e3f2fd', color: '#1565c0' };
  return { bgcolor: '#f1f2f4', color: '#6d7175' };
};

const getReportTypeColor = (type: string) => {
  const map: Record<string, { bgcolor: string; color: string }> = {
    sales:    { bgcolor: '#e8f5e9', color: '#2e7d32' },
    orders:   { bgcolor: '#e3f2fd', color: '#1565c0' },
    users:    { bgcolor: '#f3e5f5', color: '#7b1fa2' },
    delivery: { bgcolor: '#fff3e0', color: '#e65100' },
    returns:  { bgcolor: '#fff8e1', color: '#f57f17' },
    wallet:   { bgcolor: '#ede7f6', color: '#4527a0' },
  };
  return map[type?.toLowerCase()] || { bgcolor: '#f1f2f4', color: '#6d7175' };
};

// ─── Generate Dialog ──────────────────────────────────────────────────────────

interface GenerateDialogProps {
  open: boolean;
  onClose: () => void;
  onGenerated: (msg: string) => void;
  onError: (msg: string) => void;
  formats: ReportFormat[];
}

const GenerateDialog: React.FC<GenerateDialogProps> = ({ open, onClose, onGenerated, onError, formats }) => {
  const [formatId,  setFormatId]  = useState<string>('');
  const [fromDate,  setFromDate]  = useState('');
  const [toDate,    setToDate]    = useState('');
  const [errors,    setErrors]    = useState<Record<string, string>>({});
  const [generate, { isLoading }] = useGenerateReportMutation();

  const today = new Date().toISOString().split('T')[0];

  const validate = () => {
    const e: Record<string, string> = {};
    if (!formatId) e.formatId = 'Please select a report format';
    if (!fromDate) e.fromDate = 'Start date is required';
    if (!toDate)   e.toDate   = 'End date is required';
    if (fromDate && toDate && fromDate > toDate) e.toDate = 'End date must be after start date';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      const result = await generate({
        format_id: Number(formatId),
        from_date: fromDate,
        to_date:   toDate,
      }).unwrap();
      onGenerated(result.message || 'Report generated successfully!');
      handleClose();
    } catch (err: any) {
      onError(err?.data?.message || err?.message || 'Failed to generate report');
    }
  };

  const handleClose = () => {
    setFormatId(''); setFromDate(''); setToDate(''); setErrors({});
    onClose();
  };

  const selectedFormat = formats.find(f => String(f.id) === formatId);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ bgcolor: '#e3f2fd', borderRadius: '8px', p: 0.8, display: 'flex' }}>
              <PlayArrow sx={{ color: '#1565c0', fontSize: 20 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Generate Report</Typography>
          </Box>
          <IconButton onClick={handleClose} size="small" disabled={isLoading}><Close /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={2.5}>
          {/* Format Select */}
          <FormControl fullWidth error={!!errors.formatId} disabled={isLoading}>
            <InputLabel>Report Format *</InputLabel>
            <Select
              value={formatId}
              onChange={(e: SelectChangeEvent) => { setFormatId(e.target.value); setErrors(prev => ({ ...prev, formatId: '' })); }}
              label="Report Format *"
            >
              {formats.filter(f => Number(f.status) === 1).map(f => (
                <MenuItem key={f.id} value={String(f.id)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={f.report_type}
                      size="small"
                      sx={{ ...getReportTypeColor(f.report_type.toLowerCase().replace(' report', '').trim()), fontWeight: 600, fontSize: '10px', height: '20px' }}
                    />
                    <Typography variant="body2">{f.format_name}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
            {errors.formatId && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>{errors.formatId}</Typography>
            )}
          </FormControl>

          {/* Selected format info */}
          {selectedFormat && (
            <Box sx={{ p: 1.5, bgcolor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e1e3e5' }}>
              <Typography variant="caption" color="text.secondary">Export: <strong>{selectedFormat.export_type?.toUpperCase()}</strong> &nbsp;|&nbsp; Fields: <strong>{Array.isArray(selectedFormat.fields) ? selectedFormat.fields.length : 0}</strong></Typography>
            </Box>
          )}

          {/* Date range */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="From Date *"
                type="date"
                value={fromDate}
                onChange={e => { setFromDate(e.target.value); setErrors(prev => ({ ...prev, fromDate: '' })); }}
                error={!!errors.fromDate}
                helperText={errors.fromDate}
                fullWidth
                disabled={isLoading}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: today }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="To Date *"
                type="date"
                value={toDate}
                onChange={e => { setToDate(e.target.value); setErrors(prev => ({ ...prev, toDate: '' })); }}
                error={!!errors.toDate}
                helperText={errors.toDate}
                fullWidth
                disabled={isLoading}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: fromDate, max: today }}
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={isLoading} variant="outlined"
          sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: '#f44336', color: '#fff', border: 'none', fontWeight: 500, '&:hover': { bgcolor: '#d32f2f', border: 'none' } }}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isLoading}
          sx={{ textTransform: 'none', bgcolor: '#202223', borderRadius: '8px', fontWeight: 500, '&:hover': { bgcolor: '#000' } }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} color="inherit" />
              Generating...
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PlayArrow fontSize="small" /> Generate
            </Box>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

const GeneratedReports = () => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen,    setMobileOpen]    = useState(false);
  const [page,          setPage]          = useState(1);
  const [searchStr,     setSearchStr]     = useState('');
  const [tabValue,      setTabValue]      = useState(0);
  const [expandedId,    setExpandedId]    = useState<number | null>(null);
  const [generateOpen,  setGenerateOpen]  = useState(false);
  const [deletingId,    setDeletingId]    = useState<number | null>(null);
  const [filterType,    setFilterType]    = useState('');
  const [filterStatus,  setFilterStatus]  = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // APIs
  const { data: listRes, isLoading, isError, refetch } = useListReportsQuery({ report_type: filterType || undefined, status: filterStatus || undefined });
  const { data: formatsRes } = useListReportFormatsQuery();
  const [deleteReport, { isLoading: isDeleting }] = useDeleteReportMutation();

  const allReports: GeneratedReport[] = listRes?.data || [];
  const allFormats: ReportFormat[]    = formatsRes?.data || [];

  const usersPerPage = 10;

  // ── Filtered + paginated ──────────────────────────────────────────────────
  const filteredReports = useMemo(() =>
    allReports.filter(r => {
      if (!searchStr) return true;
      const sl = searchStr.toLowerCase();
      return (
        r.format_name?.toLowerCase().includes(sl)  ||
        r.report_type?.toLowerCase().includes(sl)  ||
        r.generated_by?.toLowerCase().includes(sl) ||
        r.status?.toLowerCase().includes(sl)
      );
    }),
  [allReports, searchStr]);

  const totalPages    = Math.ceil(filteredReports.length / usersPerPage);
  const paginatedList = filteredReports.slice((page - 1) * usersPerPage, page * usersPerPage);

  // Stats
  const readyCount      = allReports.filter(r => r.status === 'ready').length;
  const generatingCount = allReports.filter(r => r.status === 'generating').length;
  const failedCount     = allReports.filter(r => r.status === 'failed').length;

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleDrawerToggle  = () => setMobileOpen(v => !v);
  const handleChangePage    = (_: React.ChangeEvent<unknown>, p: number) => setPage(p);
  const handleSearch        = (e: React.ChangeEvent<HTMLInputElement>) => { setSearchStr(e.target.value); setPage(1); };
  const handleCloseSnackbar = () => setSnackbar(s => ({ ...s, open: false }));
  const handleToggleExpand  = (id: number) => setExpandedId(v => v === id ? null : id);

  const handleRefresh = async () => {
    try { await refetch(); setSnackbar({ open: true, message: 'Refreshed!', severity: 'success' }); }
    catch { setSnackbar({ open: true, message: 'Error refreshing', severity: 'error' }); }
  };

  const clearFilters = () => { setSearchStr(''); setFilterType(''); setFilterStatus(''); setPage(1); };

  const handleDelete = async (report: GeneratedReport) => {
    if (!window.confirm(`Delete report "${report.format_name}" (ID #${report.id})?`)) return;
    try {
      setDeletingId(report.id);
      const result = await deleteReport({ id: report.id }).unwrap();
      setSnackbar({ open: true, message: result.message || 'Report deleted!', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.data?.message || err?.message || 'Error deleting report', severity: 'error' });
    } finally { setDeletingId(null); }
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f1f2f4' }}>
      {/* Sidebar */}
      <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
        <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
      </Box>
      <Drawer variant="temporary" open={mobileOpen} onClose={handleDrawerToggle} ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: 250 } }}>
        <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
      </Drawer>

      {/* Main */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
        <Box sx={{ flexShrink: 0 }}><AdminHeader onToggleMenu={handleDrawerToggle} /></Box>

        <Box sx={{ flexGrow: 1, overflow: 'auto', backgroundColor: '#f1f2f4' }}>
          <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>

            {/* Page Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>Generated Reports</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="outlined" startIcon={<Refresh />} onClick={handleRefresh} disabled={isLoading}
                  sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223', borderRadius: '8px', fontWeight: 500, '&:hover': { borderColor: '#202223', bgcolor: 'transparent' } }}>
                  Refresh
                </Button>
                <Button variant="contained" startIcon={<PlayArrow />} onClick={() => setGenerateOpen(true)}
                  sx={{ textTransform: 'none', bgcolor: '#202223', borderRadius: '8px', fontWeight: 500, '&:hover': { bgcolor: '#000' } }}>
                  Generate Report
                </Button>
              </Box>
            </Box>

            {/* Stats Cards */}
            {!isLoading && !isError && allReports.length > 0 && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  { icon: <Summarize    sx={{ color: '#1565c0', fontSize: 24 }} />, bg: '#e3f2fd', value: allReports.length, label: 'Total Reports' },
                  { icon: <CheckCircle  sx={{ color: '#2e7d32', fontSize: 24 }} />, bg: '#e8f5e9', value: readyCount,        label: 'Ready' },
                  { icon: <HourglassEmpty sx={{ color: '#856404', fontSize: 24 }} />, bg: '#fff3cd', value: generatingCount, label: 'Generating' },
                  { icon: <ErrorOutline sx={{ color: '#c62828', fontSize: 24 }} />, bg: '#ffebee', value: failedCount,       label: 'Failed' },
                ].map(({ icon, bg, value, label }) => (
                  <Grid item xs={12} sm={6} md={3} key={label}>
                    <Card sx={{ borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ bgcolor: bg, borderRadius: '8px', p: 1, display: 'flex' }}>{icon}</Box>
                        <Box>
                          <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>{value}</Typography>
                          <Typography variant="body2" color="text.secondary">{label}</Typography>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}

            {/* Table Card */}
            <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
              <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}
                  sx={{ px: 2, '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: '14px', minHeight: 48, color: '#6d7175' }, '& .Mui-selected': { color: '#202223', fontWeight: 600 }, '& .MuiTabs-indicator': { backgroundColor: '#202223', height: 3 } }}>
                  <Tab label={`All Reports (${filteredReports.length})`} />
                </Tabs>
              </Box>

              {/* Filters */}
              <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5', display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField size="small" placeholder="Search by name, type, user..." value={searchStr} onChange={handleSearch}
                  sx={{ maxWidth: 320, '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white' } }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" sx={{ color: '#6d7175' }} /></InputAdornment> }} />

                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Report Type</InputLabel>
                  <Select value={filterType} onChange={(e: SelectChangeEvent) => { setFilterType(e.target.value); setPage(1); }} label="Report Type"
                    sx={{ borderRadius: '8px', bgcolor: 'white' }}>
                    {REPORT_TYPE_FILTER_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel>Status</InputLabel>
                  <Select value={filterStatus} onChange={(e: SelectChangeEvent) => { setFilterStatus(e.target.value); setPage(1); }} label="Status"
                    sx={{ borderRadius: '8px', bgcolor: 'white' }}>
                    {REPORT_STATUS_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </Select>
                </FormControl>

                <Button variant="outlined" startIcon={<FilterList />} onClick={clearFilters}
                  sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223', borderRadius: '8px', fontWeight: 500, '&:hover': { borderColor: '#202223', bgcolor: 'transparent' } }}>
                  Clear
                </Button>
              </Box>

              {/* Loading / Error / Empty States */}
              {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                  <CircularProgress size={32} sx={{ color: '#008060' }} />
                </Box>
              )}
              {isError && !isLoading && (
                <Box sx={{ p: 3, bgcolor: 'white' }}><Alert severity="error">Failed to load reports. Please try again.</Alert></Box>
              )}
              {!isLoading && !isError && paginatedList.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                  <Assessment sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>No reports found</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {searchStr || filterType || filterStatus
                      ? 'No results match your filters'
                      : 'No reports have been generated yet'}
                  </Typography>
                  {!searchStr && !filterType && !filterStatus && (
                    <Button variant="contained" startIcon={<PlayArrow />} onClick={() => setGenerateOpen(true)}
                      sx={{ textTransform: 'none', bgcolor: '#202223', borderRadius: '8px', fontWeight: 500 }}>
                      Generate First Report
                    </Button>
                  )}
                </Box>
              )}

              {/* Table */}
              {!isLoading && paginatedList.length > 0 && (
                <Box sx={{ bgcolor: 'white' }}>
                  {/* Desktop Header */}
                  {!isMobile && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: '48px 60px 1fr 130px 130px 160px 100px 80px 100px', gap: 1.5, px: 2, py: 1.5, borderBottom: '1px solid #e1e3e5', bgcolor: '#fafbfb', alignItems: 'center' }}>
                      {['', 'ID', 'Format Name', 'Report Type', 'Export', 'Date Range', 'Records', 'Status', 'Actions'].map(h => (
                        <Typography key={h} variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', textAlign: h === 'Actions' ? 'center' : 'left' }}>{h}</Typography>
                      ))}
                    </Box>
                  )}

                  {paginatedList.map((report) => {
                    const isThisDeleting = deletingId === report.id;
                    const statusInfo     = getStatusChip(report.status);
                    const isReady        = report.status === 'ready';

                    return (
                      <Box key={report.id}>
                        <Box
                          sx={!isMobile ? {
                            display: 'grid',
                            gridTemplateColumns: '48px 60px 1fr 130px 130px 160px 100px 80px 100px',
                            gap: 1.5, px: 2, py: 2, alignItems: 'center',
                            borderBottom: '1px solid #e1e3e5',
                            '&:hover': { bgcolor: '#f9fafb' },
                            transition: 'background-color 0.15s ease',
                            cursor: 'pointer',
                          } : { p: 2, borderBottom: '1px solid #e1e3e5', '&:hover': { bgcolor: '#f9fafb' }, cursor: 'pointer' }}
                          onClick={() => handleToggleExpand(report.id)}
                        >
                          {!isMobile ? (
                            <>
                              {/* Expand toggle */}
                              <IconButton size="small" onClick={e => { e.stopPropagation(); handleToggleExpand(report.id); }} sx={{ color: '#6d7175', p: 0 }}>
                                {expandedId === report.id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                              </IconButton>

                              {/* ID */}
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>#{report.id}</Typography>

                              {/* Format Name */}
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>{report.format_name}</Typography>
                                <Typography variant="caption" color="text.secondary">By: {report.generated_by}</Typography>
                              </Box>

                              {/* Report Type */}
                              <Chip
                                label={report.report_type}
                                size="small"
                                sx={{ ...getReportTypeColor(report.report_type), fontWeight: 600, fontSize: '11px', height: '24px', textTransform: 'capitalize', maxWidth: '120px' }}
                              />

                              {/* Export Type */}
                              <Chip
                                label={report.export_type?.toUpperCase() || '—'}
                                size="small"
                                sx={{ ...getExportColor(report.export_type), fontWeight: 600, fontSize: '11px', height: '24px' }}
                              />

                              {/* Date Range */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CalendarToday sx={{ fontSize: 11, color: '#6d7175', flexShrink: 0 }} />
                                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                                  {formatDate(report.from_date)}<br />{formatDate(report.to_date)}
                                </Typography>
                              </Box>

                              {/* Records */}
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>
                                {report.total_records != null ? report.total_records.toLocaleString() : '—'}
                              </Typography>

                              {/* Status */}
                              <Chip
                                icon={statusInfo.icon || undefined}
                                label={statusInfo.label}
                                size="small"
                                sx={{ bgcolor: statusInfo.bgcolor, color: statusInfo.color, fontWeight: 600, fontSize: '11px', height: '24px' }}
                              />

                              {/* Actions */}
                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                {isReady && report.download_url_pdf && (
                                  <Tooltip title="Download PDF">
                                    <IconButton size="small" component="a" href={report.download_url_pdf} target="_blank" rel="noreferrer"
                                      onClick={e => e.stopPropagation()} sx={{ color: '#c62828' }}>
                                      <PictureAsPdf fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {isReady && report.download_url_csv && (
                                  <Tooltip title="Download CSV">
                                    <IconButton size="small" component="a" href={report.download_url_csv} target="_blank" rel="noreferrer"
                                      onClick={e => e.stopPropagation()} sx={{ color: '#2e7d32' }}>
                                      <TableChart fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title="Delete">
                                  <IconButton size="small" onClick={e => { e.stopPropagation(); handleDelete(report); }}
                                    disabled={isThisDeleting || isDeleting} sx={{ color: '#d32f2f' }}>
                                    {isThisDeleting ? <CircularProgress size={18} /> : <Delete fontSize="small" />}
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </>
                          ) : (
                            /* Mobile Card */
                            <Stack spacing={1.5}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#202223' }}>{report.format_name}</Typography>
                                  <Typography variant="caption" color="text.secondary">ID: #{report.id} · By: {report.generated_by}</Typography>
                                </Box>
                                <IconButton size="small" onClick={e => { e.stopPropagation(); handleToggleExpand(report.id); }} sx={{ color: '#6d7175' }}>
                                  {expandedId === report.id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                </IconButton>
                              </Box>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                                <Chip label={report.report_type} size="small" sx={{ ...getReportTypeColor(report.report_type), fontWeight: 600, fontSize: '11px', height: '22px', textTransform: 'capitalize' }} />
                                <Chip label={report.export_type?.toUpperCase()} size="small" sx={{ ...getExportColor(report.export_type), fontWeight: 600, fontSize: '11px', height: '22px' }} />
                                <Chip icon={statusInfo.icon || undefined} label={statusInfo.label} size="small"
                                  sx={{ bgcolor: statusInfo.bgcolor, color: statusInfo.color, fontWeight: 600, fontSize: '11px', height: '22px' }} />
                              </Box>
                            </Stack>
                          )}
                        </Box>

                        {/* Expanded Detail Row */}
                        <Collapse in={expandedId === report.id} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 3, bgcolor: '#fafbfb', borderBottom: '1px solid #e1e3e5' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#202223' }}>Report Details</Typography>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                {isReady && report.download_url_pdf && (
                                  <Button size="small" startIcon={<PictureAsPdf />} component="a" href={report.download_url_pdf} target="_blank" rel="noreferrer"
                                    sx={{ textTransform: 'none', borderRadius: '6px', color: '#c62828', fontWeight: 500, bgcolor: '#ffebee', '&:hover': { bgcolor: '#ffcdd2' } }}>
                                    PDF
                                  </Button>
                                )}
                                {isReady && report.download_url_csv && (
                                  <Button size="small" startIcon={<TableChart />} component="a" href={report.download_url_csv} target="_blank" rel="noreferrer"
                                    sx={{ textTransform: 'none', borderRadius: '6px', color: '#2e7d32', fontWeight: 500, bgcolor: '#e8f5e9', '&:hover': { bgcolor: '#c8e6c9' } }}>
                                    CSV
                                  </Button>
                                )}
                                <Button size="small" color="error"
                                  startIcon={isThisDeleting ? <CircularProgress size={14} /> : <Delete />}
                                  onClick={() => handleDelete(report)}
                                  disabled={isThisDeleting || isDeleting}
                                  sx={{ textTransform: 'none', borderRadius: '6px', fontWeight: 500 }}>
                                  Delete
                                </Button>
                              </Box>
                            </Box>

                            <Grid container spacing={2} sx={{ mb: 2 }}>
                              {[
                                { label: 'Report Type',   value: report.report_type,     chip: true, chipSx: getReportTypeColor(report.report_type) },
                                { label: 'Export Format', value: report.export_type?.toUpperCase(), chip: true, chipSx: getExportColor(report.export_type) },
                                { label: 'Status',        value: statusInfo.label,       chip: true, chipSx: { bgcolor: statusInfo.bgcolor, color: statusInfo.color } },
                                { label: 'Total Records', value: report.total_records != null ? report.total_records.toLocaleString() : '—', chip: false },
                                { label: 'Generated By',  value: report.generated_by,   chip: false },
                                { label: 'Generated At',  value: formatDateTime(report.created_at), chip: false },
                                { label: 'From Date',     value: formatDate(report.from_date), chip: false },
                                { label: 'To Date',       value: formatDate(report.to_date),   chip: false },
                              ].map(({ label, value, chip, chipSx }) => (
                                <Grid item xs={6} sm={3} key={label}>
                                  <Card sx={{ p: 1.5, borderRadius: '8px', border: '1px solid #e1e3e5', bgcolor: 'white', height: '100%' }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{label}</Typography>
                                    {chip && chipSx
                                      ? <Chip label={value} size="small" sx={{ ...(chipSx as any), fontWeight: 600, fontSize: '11px', height: '22px', textTransform: 'capitalize' }} />
                                      : <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223', fontSize: '13px' }}>{value}</Typography>
                                    }
                                  </Card>
                                </Grid>
                              ))}
                            </Grid>

                            {/* Download Buttons Row */}
                            {isReady && (report.download_url_pdf || report.download_url_csv) && (
                              <Card sx={{ p: 2, borderRadius: '8px', border: '1px solid #e1e3e5', bgcolor: 'white' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                  <FileDownload sx={{ fontSize: 16, color: '#202223' }} />
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#202223' }}>Downloads</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                  {report.download_url_pdf && (
                                    <Button variant="outlined" size="small" startIcon={<PictureAsPdf />}
                                      component="a" href={report.download_url_pdf} target="_blank" rel="noreferrer"
                                      sx={{ textTransform: 'none', borderRadius: '8px', borderColor: '#c62828', color: '#c62828', fontWeight: 500, '&:hover': { bgcolor: '#ffebee', borderColor: '#c62828' } }}>
                                      Download PDF
                                    </Button>
                                  )}
                                  {report.download_url_csv && (
                                    <Button variant="outlined" size="small" startIcon={<TableChart />}
                                      component="a" href={report.download_url_csv} target="_blank" rel="noreferrer"
                                      sx={{ textTransform: 'none', borderRadius: '8px', borderColor: '#2e7d32', color: '#2e7d32', fontWeight: 500, '&:hover': { bgcolor: '#e8f5e9', borderColor: '#2e7d32' } }}>
                                      Download CSV
                                    </Button>
                                  )}
                                </Box>
                              </Card>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Card>

            {totalPages > 1 && !isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination count={totalPages} page={page} onChange={handleChangePage}
                  sx={{ '& .MuiPaginationItem-root': { borderRadius: '8px', fontWeight: 500 }, '& .Mui-selected': { bgcolor: '#202223 !important', color: 'white' } }} />
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Generate Report Dialog */}
      <GenerateDialog
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
        onGenerated={(msg) => { setSnackbar({ open: true, message: msg, severity: 'success' }); refetch(); }}
        onError={(msg)      => setSnackbar({ open: true, message: msg, severity: 'error' })}
        formats={allFormats}
      />

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GeneratedReports;